<template>
    <PageBody v-if="page.uuid" :page="page" @updateIframeHeight="updateHeight" />
</template>

<script>
import { waitForMe } from "@/shared/utils/helpers";
import PageBody from "@/views/tools/page/PageBody.vue";
import { resizeAllGridItems, setFontFamily, updateIframeHeight, searchQuery } from "@/shared/utils";
import { preparePage } from "@/shared/projects/prepareData";
import { usePreviewLanguage } from "@/shared/utils/router/usePreviewLanguage";
import { projectsService } from "@/shared/projects/projects.service";

export default {
    name: "PagePage",
    components: {
        PageBody,
    },
    data() {
        return {
            page: {},
            windowWidth: window.width,
            currentFont: "Inter",
            waiting: true,
        };
    },
    setup() {
        const { setLanguageIfNoQuery } = usePreviewLanguage();
        return { setLanguageIfNoQuery };
    },
    async mounted() {
        const { projectUuid } = this.$route.params;

        try {
            this.page = await this.loadPage(projectUuid);
            if (this.page.uuid) {
                //in case there is no lang in query chose project ownwer's language
                this.setLanguageIfNoQuery(this.page?.user?.language);

                this.adjustIframe();
                //this is a hack to make sure the grid items are resized correctly
                await waitForMe(300);
                resizeAllGridItems();
                this.updateHeight();
            }
            // settle the settings:
            this.setupPageSettings();
        } finally {
            this.waiting = false;
        }

        window.addEventListener("resize", this.resizeIframe);
    },
    beforeUnmount() {
        window.removeEventListener("resize", this.resizeIframe);
    },
    methods: {
        async loadPage(uuid) {
            try {
                const page = await projectsService.getOneForRender({ tool: "page", uuid });
                return preparePage(page);
            } catch (e) {
                return {};
            }
        },
        async adjustIframe() {
            this.currentFont = setFontFamily(this.page.fontFamily, this.currentFont);
            await this.$nextTick();
            resizeAllGridItems();
            this.updateHeight();
            searchQuery();
        },
        updateHeight() {
            updateIframeHeight("page", this.page.uuid);
        },
        resizeIframe() {
            if (window.innerWidth !== this.windowWidth) {
                this.adjustIframe();
                this.windowWidth = window.innerWidth;
            }
        },
        setupPageSettings() {
            const { title = "easypage", favicon = "" } = this.page.settings || {};
            window.document.title = title;
            let icon = document.querySelector("link[rel~='icon']");
            if (!icon) {
                icon = document.createElement("link");
                icon.rel = "icon";
                document.head.appendChild(icon);
            }
            icon.href =
                favicon ||
                "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAUxSURBVHgBrVddTFtVHP+f2+LGXAuNkUkbXIkPI3EqJvAwYjLAxGQ+yOVpS6YB4keGJlKCgIlOSpgPyMwKD1OXGCCb0RkTWh/mNHFgjDMRk2GiCTwYigRQ/OgHBBi09/j/n3Iul97bUmC/pOk9/3Pu/X9/HAY5YtqnFh7MA5VpyknGeDnj4OUMCmkPn6P4HGacTXCbNrayDqHSQDCay3fZTgcWOlUvSzCfAtAgGeb0Yc6Gknatu7g3GM56LtMGaXy/XeniwH2wDyjAAssJrTuTRSwFIK2VBBvFXS/cC3AIa3ZeY2UNkwCL7Wo519hIJubxhAY3/lqG2bUNiG9o4MxTBP244yD+7oOS/DzIJARTeH1RX3AiowCZNCemn87H4ObiMtz+bxWy4bjjALx81AVn3E5LIdItoQtAPj9kY3fSmf+ADFt+W4DZ1YROK3Q4wOv2iH9CdGkJJqYmt/EiS/QcexBOFR02CbGS5E/KmLBL+mbAeY1nL/7+L/ThTzJteK4O1NqnobqiEqww9vM4DIWCMPxlCAXegMaJeXjjkQegHX+wpbL3MPLCp9bUEjZNn2TTmZgTw8GeC0LrXBCen4PW93ohOHpLrMkSr6BbjNBsvJRcISII/e43bl75I6Iz7zr3Kox+PJgzcwKdHQkMgO/sC2J9fupvjJ2VbWeYBiK9WQR9v25nEblBpqv4PmUMtaYWWvAj3R9eFmv6KLmi/9pV9PmU0JRANDrbUKcibR6azr8l6I3osiF0B7mmypUPI5UlRhmiKwleal/D8qrwLarUnEBMal5q0tehW9/CUY8HfH29Jq3J3AVOB0TjS4IhQf4TbkdWRTY57YokidKuKBqrlpQYHrg+H9dfkhoSyo+VwcnKShEP0199Df7mZn2vqznlJrUGAxTPeN1usMINTGMjqK/YMQyf0KVM85NEy9nnIdDxpr4mH3edew21XcYUjIMf40RXy+FEAb8BP7qt+4PLkA0KNjX7ZlcT+HXprunQYM+7wpcEMvMvk5PC16RlXW2tEIBAtYBiw+txY7qqQigSVMaDFdDzXsXY4SgAjbjU0akzJ1CgkbClp56BmhebYDgUwrgYFc+up07QBwVzCXr3Ununvn44354uQ6GJYkQMAyodKc3cqO01jPCgoFFcUAxYFajYcuobJRhxVa5Dpn222FYfkVagDLhoyALCnc+/EAG4F4yNj+tZNPDoQ3DaY+oPUXJBWK6okaQjZeog7BZUjutbXxfPVAMsmFMZDtvaqspOMGDlRCg6YIfh2Sjc1bYKw9r6ugi+GUzJQqdzx4pIWje98zYEPrkq3iWlPnq8GArybKaznLObbKFdbUQzDEoilc0rM5GMDEiA6ooK4ZaCzW4YE91wCgvPT6ISSpzGlnyhrMhYfNIlaBSleMPGpmUcUCbU/jgjqtZ+QdpT+c0kAJZil+LCvqwBH5ZE6uMdxva5D1BdqR+ftVQGzT9EM4EQjSchYNykiab9Hgrx2VzMLEBS66Z/IUBxIBjmnPcbD5gGiT2CvpM+C3DG+4knPesjmWjLFiPZ9bk41od/cAhNwG5AhWfgsSPm4oMj2ZH3R0rlcvtQ6sPJyGY9jpMgNKhY9QsjKOdpDjzjKTAHHw2lSRxKAxZDqcROYzllCQkhBZGjOQXvs8g4c8rlMJZLZLPEnmCheVYBJP5sUwOMsRbYD28MODSaf1dXMyPIGmADv8JYA+QIui0nsLYwTG8rrXclgIS4niug4hvVCk5RePtNu55zTGVlgivad3hrC+Z6Pf8fya1fwseJ9OcAAAAASUVORK5CYII=";
        },
    },
};
</script>
